import { OptionValues } from '../types/optionTypes';

export interface LogicContext {
    values: OptionValues;
    product?: any; // Current product info
}

interface LogicRule {
    cl_field: string;
    cl_relation: string;
    cl_val: string;
    cl_field_sub?: string;
}

interface RelationGroup {
    operator: string | boolean;
    rules: LogicRule | RelationGroup | (LogicRule | RelationGroup)[];
}

export const evaluateCondition = (
    relations: RelationGroup[] | boolean | undefined,
    context: LogicContext
): boolean => {
    if (!relations || (typeof relations === 'boolean' && !relations) || (Array.isArray(relations) && relations.length === 0)) {
        return true; // No relations means no restrictions (usually)
    }

    // Top level relations array is usually treated as OR or AND depending on implementation,
    // but looking at JSON, it seems to be a list of groups.
    // Let's assume if ANY group matches, it's true (OR), or ALL (AND).
    // In many systems, the top level is implicit AND, but let's check the operator of the first item if it exists.

    // Actually, looking at the JSON: "relations":[{"operator":false,"rules":[...]}]
    // The top level seems to be a container.

    if (relations === true) return true;

    return (relations as RelationGroup[]).every((group: RelationGroup) => evaluateGroup(group, context));
};

const evaluateGroup = (group: RelationGroup, context: LogicContext): boolean => {
    const { operator, rules } = group;

    // If rules is an array, we evaluate based on operator
    if (Array.isArray(rules)) {
        if (!rules.length) return true;

        if (operator === 'AND' || operator === 'and') {
            return rules.every(rule => evaluateRuleOrGroup(rule, context));
        } else {
            // Default to OR (based on Print.json structure where operator is false but implies alternatives)
            return rules.some(rule => evaluateRuleOrGroup(rule, context));
        }
    } else {
        // Single rule object
        return evaluateRuleOrGroup(rules, context);
    }
};

const evaluateRuleOrGroup = (item: any, context: LogicContext): boolean => {
    if ('operator' in item && 'rules' in item) {
        return evaluateGroup(item as RelationGroup, context);
    } else {
        return evaluateRule(item as LogicRule, context);
    }
};

const evaluateRule = (rule: LogicRule, context: LogicContext): boolean => {
    const { cl_field, cl_relation, cl_val, cl_field_sub } = rule;
    let actualValue: any;

    // Special fields
    if (cl_field === 'product_skus') {
        // Check product handle or tag or SKU
        // Assuming context.product has handle or tags
        // For now, let's check handle as a fallback if SKU is missing
        actualValue = context.product?.handle || '';

        // If cl_val is "customalminiumovermat", we might need to check if the product matches this "type"
        // This is a bit specific to the app's logic.
        // For the purpose of this task, let's assume the product object has a way to identify this.
        // Or we check against the selected product's handle.
    } else if (cl_field === 'custom_attribute') {
        // Check specific attribute
        if (cl_field_sub === '額縁サイズ') {
            // This might come from the product variants or tags
            // For now, let's assume it checks the selected size variant's title or option
            actualValue = context.product?.selectedVariant?.title || '';
        } else if (cl_field_sub === '加工') {
            // Maybe checks tags?
            actualValue = context.product?.tags || [];
        } else {
            actualValue = '';
        }
    } else {
        // Standard field value
        actualValue = context.values[cl_field];
    }

    // Normalize values
    const targetVal = cl_val;

    switch (cl_relation) {
        case 'is':
            if (Array.isArray(actualValue)) {
                return actualValue.includes(targetVal);
            }
            return String(actualValue) === String(targetVal);

        case 'is_not':
            if (Array.isArray(actualValue)) {
                return !actualValue.includes(targetVal);
            }
            return String(actualValue) !== String(targetVal);

        case 'is_empty':
            return !actualValue || actualValue === '' || (Array.isArray(actualValue) && actualValue.length === 0);

        case 'is_not_empty':
            return !!actualValue && actualValue !== '' && (!Array.isArray(actualValue) || actualValue.length > 0);

        case 'contains':
            return String(actualValue).includes(targetVal);

        case 'not_contains':
            return !String(actualValue).includes(targetVal);

        case 'greater_than':
            return Number(actualValue) > Number(targetVal);

        case 'less_than':
            return Number(actualValue) < Number(targetVal);

        default:
            console.warn(`Unknown relation: ${cl_relation}`);
            return false;
    }
};
